import './CustomPacingScheduleButton.scss';

import { IconCalendar } from '@hulu-react-style-components/icons';
import { User } from 'mission-control-common-components';
import React, { useCallback, useMemo, useState } from 'react';

import BasicSelect from '../../../common/BasicSelect';
import ModalPopup from '../../../common/ModalPopup';
import type { PermissionsNames } from '../../../constants';
import { useDeleteCustomPacings } from '../../../hooks/useDeleteCustomPacings';
import bem from '../../../utils/bem';
import type { TraffickingPageDrawerData } from '../TraffickingPage/hooks/makePageDrawerPlugin';
import { DrawerType } from '../TraffickingPage/hooks/makePageDrawerPlugin';
import type { LineItemModel } from '../TraffickingPage/modelConverters';
import { BUTTON_TEXT, DEFAULT_REMOVE_SCHEDULE_MODAL_PROPS, REMOVE_SCHEDULE_BUTTON_TOOLTIP_MESSAGE } from './constants';
import CustomPacingScheduleDropdownButtons from './CustomPacingScheduleDropdownButtons';
import type { CustomPacingPacingScheduleDropdownButtonConfig } from './types';
import { CustomPacingScheduleDropdownButtonAriaLabel, CustomPacingScheduleDropdownButtonLabel } from './types';
import { getRemoveScheduleModalMessage } from './utils';

const [block, element] = bem('custom-pacing-schedule-button');

export type Props = {
  selectedLineItems: LineItemModel[];
  openTraffickingPageDrawer: (data: TraffickingPageDrawerData) => void;
};

const CustomPacingScheduleButton = ({ selectedLineItems, openTraffickingPageDrawer }: Props): React.JSX.Element => {
  const permissions = User.getPermissions<PermissionsNames>();
  const [isRemoveScheduleModalOpen, setIsRemoveScheduleModalOpen] = useState<boolean>(false);

  const lineItemsIdsWithCustomPacing = useMemo(
    () =>
      selectedLineItems.reduce<string[]>((acc, { id, customPacingApplied }) => {
        if (customPacingApplied) acc.push(id);

        return acc;
      }, []),
    [selectedLineItems]
  );

  const isRemoveScheduleButtonDisabled = !lineItemsIdsWithCustomPacing.length || !permissions?.removeCustomPacing;

  const { handleDelete, loading } = useDeleteCustomPacings();

  const handleOpenDrawer = useCallback((): void => {
    openTraffickingPageDrawer({
      drawerType: DrawerType.CUSTOM_PACING_SCHEDULE,
      lineItemIdList: selectedLineItems.map(({ id }) => id),
    });
  }, [selectedLineItems, openTraffickingPageDrawer]);

  const onHandleOpenRemoveScheduleConfirmationModal = useCallback(() => {
    setIsRemoveScheduleModalOpen(true);
  }, []);

  const onHandleCloseRemoveScheduleConfirmationModal = useCallback(() => {
    setIsRemoveScheduleModalOpen(false);
  }, []);

  const onRemoveCustomPacing = useCallback(async (): Promise<void> => {
    await handleDelete(lineItemsIdsWithCustomPacing);

    onHandleCloseRemoveScheduleConfirmationModal();
  }, [handleDelete, onHandleCloseRemoveScheduleConfirmationModal, lineItemsIdsWithCustomPacing]);

  const customPacingScheduleDropdownButtonsConfig: CustomPacingPacingScheduleDropdownButtonConfig[] = useMemo(() => {
    return [
      {
        id: 1,
        label: CustomPacingScheduleDropdownButtonLabel.SET_SCHEDULE,
        onClick: handleOpenDrawer,
        isDisabled: !permissions?.applyCustomPacing,
        ariaLabel: CustomPacingScheduleDropdownButtonAriaLabel.SET_SCHEDULE,
        dataTestId: 'set-custom-pacing-schedule-button',
      },
      {
        id: 2,
        label: CustomPacingScheduleDropdownButtonLabel.REMOVE_SCHEDULE,
        isDisabled: isRemoveScheduleButtonDisabled,
        ariaLabel: CustomPacingScheduleDropdownButtonAriaLabel.REMOVE_SCHEDULE,
        dataTestId: 'remove-custom-pacing-schedule-button',
        tooltipProps: {
          id: 'remove-custom-pacing-tooltip',
          message: REMOVE_SCHEDULE_BUTTON_TOOLTIP_MESSAGE,
          isDisabled: !isRemoveScheduleButtonDisabled,
        },
        onClick: onHandleOpenRemoveScheduleConfirmationModal,
      },
    ];
  }, [
    handleOpenDrawer,
    permissions?.applyCustomPacing,
    isRemoveScheduleButtonDisabled,
    onHandleOpenRemoveScheduleConfirmationModal,
  ]);

  const getPacingScheduleOptionsRenderer = useCallback(
    (toggleOpen: () => void): React.JSX.Element => {
      return (
        <CustomPacingScheduleDropdownButtons
          toggleOpen={toggleOpen}
          options={customPacingScheduleDropdownButtonsConfig}
        />
      );
    },
    [customPacingScheduleDropdownButtonsConfig]
  );

  return (
    <>
      <BasicSelect
        buttonText={BUTTON_TEXT}
        hideDefaultRightIcon={true}
        selectButtonClassName={block()}
        leftIcon={<IconCalendar />}
        overlayClassName={`${block()} ${element('tooltip')}`}
        optionsComponent={getPacingScheduleOptionsRenderer}
      />
      <ModalPopup
        visible={isRemoveScheduleModalOpen}
        onOk={onRemoveCustomPacing}
        modalProps={{
          ...DEFAULT_REMOVE_SCHEDULE_MODAL_PROPS,
          okButtonProps: {
            loading: loading,
          },
        }}
        modalMessage={getRemoveScheduleModalMessage(lineItemsIdsWithCustomPacing.length)}
        onCancel={onHandleCloseRemoveScheduleConfirmationModal}
      />
    </>
  );
};

export default CustomPacingScheduleButton;
